import type { Dictionary } from "lodash";
import flatMap from "lodash/flatMap";
import noop from "lodash/noop";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { RouteObject } from "react-router-dom";

const NavContext = createContext<{
  currentPath: string;
  params: Dictionary<string>;
  navigate: (newPath: string, params?: Dictionary<string>) => void;
}>({
  currentPath: "",
  params: {},
  navigate: noop,
});

export type OurRoute = {
  fullPath: string;
  element?: React.ReactNode;
};

export type InitialRoute = {
  path: string;
  params?: Dictionary<string>;
};

export function Router({
  defaultRoute,
  initialParams,
  routes,
}: Readonly<{
  defaultRoute: string;
  initialParams?: Dictionary<string>;
  routes: OurRoute[];
}>) {
  const [currentPath, setCurrentPath] = useState(defaultRoute);
  const [currentParams, setCurrentParams] = useState<Dictionary<string>>(
    initialParams ?? {}
  );

  const navigate = useCallback(
    (newPath: string, params: Dictionary<string> = {}) => {
      if (!routes.some((route) => route.fullPath === newPath)) {
        return;
      }

      setCurrentParams(() => params);
      setCurrentPath(() => newPath);
    },
    [routes]
  );

  const contextValue = useMemo(
    () => ({ params: currentParams, currentPath, navigate }),
    [currentParams, currentPath, navigate]
  );

  return (
    <NavContext.Provider value={contextValue}>
      <CurrentRoute routes={routes} />
    </NavContext.Provider>
  );
}

function CurrentRoute({ routes }: Readonly<{ routes: OurRoute[] }>) {
  const context = useContext(NavContext);
  const theRoute = routes.find((r) => r.fullPath === context.currentPath);

  return <Route route={theRoute} />;
}

function Route({ route }: Readonly<{ route?: OurRoute }>) {
  if (!route) {
    return <div>Route not found</div>;
  }

  return <>{route.element}</>;
}

export function createRouter({
  defaultRoute,
  routes,
}: {
  defaultRoute: string;
  routes: RouteObject[];
}) {
  const flattened = flatMap(routes, (route) => __flatten(route));
  return {
    routes: flattened,
    Router: ({ initialRoute }: Readonly<{ initialRoute?: InitialRoute }>) => {
      const isKnownRoute =
        !!initialRoute &&
        flattened.some((r) => r.fullPath === initialRoute.path);

      return (
        <Router
          routes={flattened}
          defaultRoute={isKnownRoute ? initialRoute.path : defaultRoute}
          initialParams={isKnownRoute ? initialRoute.params : undefined}
        />
      );
    },
  };
}

function __flatten(parent: RouteObject, currentPath: string = "/"): OurRoute[] {
  const thisSegmentPath = __buildPath(currentPath, parent).replaceAll(
    "//",
    "/"
  );

  return [
    {
      fullPath: thisSegmentPath,
      element: parent.element,
    },
    ...flatMap(parent.children, (c) => __flatten(c, thisSegmentPath)),
  ];
}

function __buildPath(currentPath: string, parent: RouteObject) {
  if (currentPath.endsWith("/") || parent.path?.startsWith("/")) {
    return currentPath + parent.path;
  }

  return currentPath + "/" + parent.path;
}

export function useNavigate() {
  const context = useContext(NavContext);
  return context.navigate;
}

export function useLocation() {
  const context = useContext(NavContext);
  return {
    pathname: context.currentPath,
  };
}

export function useParams<T>() {
  const context = useContext(NavContext);
  return context.params as T;
}
